/* eslint-disable max-lines */
import { formatFacilityWithState } from 'common/utils/facilityLabel';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { sumBy } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { lotSummaryApi } from 'api/inventory';
import colors from 'common/constants/colors';
import useRoles from 'common/hooks/useRoles';
import { handleError } from 'store/thunks/errorHandlerThunks';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import ManualAdjustmentDialog from './ManualAdjustmentDialog';
import LotOffageRecordsDialog from './LotOffageRecordsDialog';
import RecallAllocationsDialog from './RecallAllocationsDialog';

const HEADER_BG = '#0d1f35';
const CHIP_SX = { color: '#fff', fontWeight: 600 };
const DARK_SWITCH_SX = {
  '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.2)', opacity: '1 !important' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    bgcolor: '#42a5f5',
    opacity: '1 !important',
  },
  '& .MuiSwitch-thumb': { color: '#fff' },
};
const COL_HEADER_SX = {
  fontWeight: 600,
  fontSize: 11,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  py: 1,
};

const statTestId = (prefix, label) =>
  `${prefix}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const StatColumn = ({ label, value, color, breakdown }) => (
  <Box
    data-testid={statTestId('stat', label)}
    sx={{
      flex: 1,
      px: 2,
      py: 1.5,
      borderRight: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderRight: 'none' },
    }}
  >
    <Typography
      variant='caption'
      sx={{
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        display: 'block',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant='h5'
      sx={{ fontWeight: 700, color: color ?? 'text.primary', lineHeight: 1.3 }}
    >
      {value}
    </Typography>
    {breakdown && (
      <Box data-testid={`${statTestId('stat', label)}-breakdown`} sx={{ mt: 0.25 }}>
        {breakdown.map((part) => (
          <Box
            key={part.label}
            data-testid={`${statTestId('stat', label)}-${statTestId('part', part.label)}`}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
          >
            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: 11 }}>
              {part.label}
            </Typography>
            <Typography variant='caption' sx={{ fontWeight: 700, fontSize: 11 }}>
              {part.value}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);
StatColumn.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  color: PropTypes.string,
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.number.isRequired })
  ),
};
StatColumn.defaultProps = { color: undefined, breakdown: undefined };

// Stock reserved by WIP is still on the shelf, so only remaining 0 with no WIP has none.
const hasStockOnShelf = (lot) => (lot.remainingQty ?? 0) > 0 || (lot.wipQty ?? 0) > 0;

const LotStatusChip = ({ lot }) => {
  if (lot.isExpired) return <Chip size='small' color='error' label='Expired' sx={CHIP_SX} />;
  // POM-6559 - "Active" on an empty lot read as stock that is not there.
  if (!hasStockOnShelf(lot)) return <Chip size='small' label='No stock' sx={{ fontWeight: 600 }} />;
  if (lot.isExpiringSoon)
    return (
      <Chip
        size='small'
        label='Expiring Soon'
        sx={{ bgcolor: '#FFC107', color: '#212121', fontWeight: 600 }}
      />
    );
  return <Chip size='small' color='success' label='Active' sx={CHIP_SX} />;
};
LotStatusChip.propTypes = {
  lot: PropTypes.shape({
    isExpired: PropTypes.bool,
    isExpiringSoon: PropTypes.bool,
    remainingQty: PropTypes.number,
    wipQty: PropTypes.number,
  }).isRequired,
};

const getDayColor = (lot) => {
  if (lot.isExpired) return 'error.main';
  if (lot.isExpiringSoon) return 'warning.main';
  return 'text.primary';
};

// The number when there is some, a greyed dash when there is none.
const QuantityOrDashCell = ({ quantity }) => {
  const hasQuantity = (quantity ?? 0) > 0;
  return (
    <TableCell sx={{ color: hasQuantity ? 'text.primary' : 'text.disabled' }}>
      {hasQuantity ? quantity : '—'}
    </TableCell>
  );
};

QuantityOrDashCell.propTypes = {
  quantity: PropTypes.number,
};

QuantityOrDashCell.defaultProps = {
  quantity: null,
};

// POM-6561 - the number opens the lot's offage records; a lot with none keeps the dash.
const OffageCell = ({ lot, onShowOffage }) => {
  if ((lot.offageQty ?? 0) <= 0) return <QuantityOrDashCell quantity={lot.offageQty} />;
  return (
    <TableCell sx={{ py: 0.5 }}>
      <Button
        size='small'
        onClick={() => onShowOffage(lot)}
        aria-label={`${lot.offageQty} — show offage records for lot ${lot.lotNumber}`}
        sx={{ minWidth: 0, fontWeight: 700, textDecoration: 'underline' }}
      >
        {lot.offageQty}
      </Button>
    </TableCell>
  );
};
OffageCell.propTypes = {
  lot: PropTypes.shape({ lotNumber: PropTypes.string, offageQty: PropTypes.number }).isRequired,
  onShowOffage: PropTypes.func.isRequired,
};

const LotTable = ({ lots, isLockForCount, onAdjust, onShowOffage }) => {
  const hasAccess = useRoles();

  if (lots.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ py: 2, px: 1 }}>
        No lots found.
      </Typography>
    );
  }
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          {[
            'LOT #',
            'EXPIRATION',
            'REMAINING',
            'WIP',
            SYSTEM_DEFICIT.toUpperCase(),
            'OFFAGE',
            'DAYS',
            'STATUS',
            '',
          ].map((col) => (
            <TableCell key={col} sx={COL_HEADER_SX}>
              {col}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {lots.map((lot) => (
          <TableRow key={lot.inventoryLotId} data-testid={`lot-row-${lot.inventoryLotId}`} hover>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{lot.lotNumber}</TableCell>
            <TableCell>
              {lot.expirationDate ? format(new Date(lot.expirationDate), 'MM/dd/yyyy') : '—'}
            </TableCell>
            <TableCell>{lot.remainingQty}</TableCell>
            {/* Reserved to orders not yet shipped. Shown beside REMAINING because the two
                together are what the shelf owes: 30 remaining with 30 WIP is fully
                promised out. An unverified claim counts here too until its order ships. */}
            <QuantityOrDashCell quantity={lot.wipQty} />
            {/* POM-6094 - its own column, never folded into REMAINING. Shown as the positive
                magnitude the API returns: the column heading already says it is a deficit,
                and a minus sign on top of that read as negative stock. */}
            <TableCell
              sx={{
                color: lot.hasUnverifiedClaim ? 'warning.dark' : 'text.disabled',
                fontWeight: lot.hasUnverifiedClaim ? 700 : 400,
              }}
            >
              {lot.hasUnverifiedClaim ? lot.unverifiedQty : '—'}
            </TableCell>
            {/* POM-6559 - units this lot lost. */}
            <OffageCell lot={lot} onShowOffage={onShowOffage} />
            <TableCell
              sx={{
                color: getDayColor(lot),
                fontWeight: lot.isExpired || lot.isExpiringSoon ? 700 : 400,
              }}
            >
              {/* POM-6096 made the BUD optional, so this is null for a lot recorded at
                  fulfilment. Null means nothing to judge, not zero days - rendering it
                  arithmetically printed the string 'nulld'. */}
              {lot.daysUntilExpiry == null && '—'}
              {lot.daysUntilExpiry != null &&
                (lot.daysUntilExpiry < 0
                  ? `${Math.abs(lot.daysUntilExpiry)}d ago`
                  : `${lot.daysUntilExpiry}d`)}
            </TableCell>
            <TableCell>
              <LotStatusChip lot={lot} />
            </TableCell>
            <TableCell align='right' sx={{ py: 0.5 }}>
              {!lot.isExpired && hasAccess.manualInventoryAdjust && (
                <Tooltip title={isLockForCount ? 'Locked for physical count' : 'Manual Adjustment'}>
                  <span>
                    <IconButton
                      size='small'
                      onClick={() => onAdjust(lot)}
                      disabled={isLockForCount}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <EditNoteIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
LotTable.propTypes = {
  lots: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLockForCount: PropTypes.bool,
  onAdjust: PropTypes.func.isRequired,
  onShowOffage: PropTypes.func.isRequired,
};
LotTable.defaultProps = { isLockForCount: false };

const PharmacyInlineStat = ({ label, value, color }) => (
  <Box
    data-testid={statTestId('pharmacy-stat', label)}
    sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}
  >
    <Typography
      variant='caption'
      sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography
      variant='caption'
      sx={{ fontWeight: 700, color: color ?? 'text.primary', fontSize: 13 }}
    >
      {value}
    </Typography>
  </Box>
);
PharmacyInlineStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  color: PropTypes.string,
};
PharmacyInlineStat.defaultProps = { color: undefined };

const getPharmacyDotColor = (result) => {
  if ((result.expiredWithStockCount ?? 0) > 0) return 'error.main';
  if ((result.expiringSoonCount ?? 0) > 0) return 'warning.main';
  return 'success.main';
};

const PharmacySection = ({ item, result, onAdjust }) => {
  const [expanded, setExpanded] = useState(true);
  const [offageLot, setOffageLot] = useState(null);
  const dotColor = getPharmacyDotColor(result);

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.25,
          px: 1,
          bgcolor: 'action.hover',
          borderRadius: 1,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? (
          <ExpandLessIcon fontSize='small' sx={{ color: 'text.secondary', flexShrink: 0 }} />
        ) : (
          <ExpandMoreIcon fontSize='small' sx={{ color: 'text.secondary', flexShrink: 0 }} />
        )}
        <Box
          sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }}
        />
        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
          {formatFacilityWithState(item.fulfillingPharmacy.name, item.fulfillingPharmacy.state)}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          Pharmacy
        </Typography>
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            gap: 1.5,
            flexWrap: 'nowrap',
            flexShrink: 0,
            justifyContent: 'flex-end',
          }}
        >
          <PharmacyInlineStat label='ON HAND' value={result.totalPhysicalQty} />
          <PharmacyInlineStat label='AVAILABLE' value={result.totalLottedQty} />
          {/* Was wired to nonLottedQty and labelled WIP, which reported unlotted on-hand
              stock as though it were reserved quantity - two unrelated numbers. */}
          <PharmacyInlineStat label='WIP' value={result.totalWipQty ?? 0} />
          <PharmacyInlineStat
            label={SYSTEM_DEFICIT.toUpperCase()}
            value={result.totalUnverifiedQty}
            color={(result.totalUnverifiedQty ?? 0) > 0 ? 'warning.dark' : undefined}
          />
          <PharmacyInlineStat
            label='EXPIRING'
            value={result.expiringSoonCount}
            color={(result.expiringSoonCount ?? 0) > 0 ? 'warning.main' : undefined}
          />
          <PharmacyInlineStat
            label='EXPIRED'
            value={result.expiredWithStockCount}
            color={(result.expiredWithStockCount ?? 0) > 0 ? 'error.main' : undefined}
          />
        </Box>
      </Box>
      {expanded && (
        <Box sx={{ px: 1 }}>
          <LotTable
            lots={result.lots ?? []}
            isLockForCount={item.isLockForCount}
            onAdjust={(lot) => onAdjust(lot, item)}
            onShowOffage={setOffageLot}
          />
        </Box>
      )}
      <LotOffageRecordsDialog lot={offageLot} item={item} onClose={() => setOffageLot(null)} />
    </Box>
  );
};
PharmacySection.propTypes = {
  item: PropTypes.shape({
    fulfillingPharmacy: PropTypes.shape({ name: PropTypes.string, state: PropTypes.string }),
    isLockForCount: PropTypes.bool,
  }).isRequired,
  result: PropTypes.shape({
    totalPhysicalQty: PropTypes.number,
    totalLottedQty: PropTypes.number,
    expiringSoonCount: PropTypes.number,
    expiredWithStockCount: PropTypes.number,
    totalUnverifiedQty: PropTypes.number,
    unverifiedLotCount: PropTypes.number,
    totalWipQty: PropTypes.number,
    totalUnverifiedWipQty: PropTypes.number,
    totalUnverifiedPendingReviewQty: PropTypes.number,
    lots: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onAdjust: PropTypes.func.isRequired,
};

const LotSummaryDrawer = ({ items, onClose, onAdjusted }) => {
  const dispatch = useDispatch();
  const hasAccess = useRoles();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeDepleted, setIncludeDepleted] = useState(false);
  // POM-6087 - narrows to the lots carrying a claim. The other two switches widen, so
  // "only these" could not be asked before: a drug with forty lots and two claims meant
  // reading forty rows looking for the orange number.
  const [onlyUnverified, setOnlyUnverified] = useState(false);
  const [includeExpired, setIncludeExpired] = useState(true);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [recallOpen, setRecallOpen] = useState(false);

  const fetchGenRef = useRef(0);

  const isOpen = items.length > 0;
  const multi = items.length > 1;
  const firstItem = items[0];

  const fetchSummaries = useCallback(() => {
    if (!isOpen) return;
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    setLoading(true);
    Promise.all(
      items.map((item) =>
        lotSummaryApi({
          inventoryItemId: item.inventoryItemId,
          ndc: item.ndc,
          fulfillingPharmacyId: item.fulfillingPharmacy.fulfillingPharmacyId,
          filter: { includeDepleted, includeExpired, onlyUnverified },
        }).then((result) => ({ item, result }))
      )
    )
      .then((results) => {
        if (fetchGenRef.current === gen) setSummaries(results.filter((r) => r.result !== null));
      })
      .catch((error) => {
        if (fetchGenRef.current === gen) dispatch(handleError({ error }));
      })
      .finally(() => {
        if (fetchGenRef.current === gen) setLoading(false);
      });
  }, [items, isOpen, includeDepleted, includeExpired, onlyUnverified, dispatch]);

  useEffect(() => {
    setSummaries([]);
    fetchSummaries();
  }, [fetchSummaries]);

  const handleAdjustSuccess = useCallback(() => {
    setSummaries([]);
    fetchSummaries();
    onAdjusted?.();
  }, [fetchSummaries, onAdjusted]);

  // A recall moves stock AND rewinds orders, so everything the drawer shows is stale.
  const handleRecallSuccess = useCallback(() => {
    setSummaries([]);
    fetchSummaries();
    onAdjusted?.();
  }, [fetchSummaries, onAdjusted]);

  const aggregates = useMemo(() => {
    if (summaries.length === 0) return null;
    const totalOnHand = sumBy(summaries, (s) => s.result.totalPhysicalQty ?? 0);
    const totalLotted = sumBy(summaries, (s) => s.result.totalLottedQty ?? 0);
    const nearestLot =
      summaries
        .flatMap((s) => s.result.lots ?? [])
        .filter((l) => !l.isExpired && (l.remainingQty ?? 0) > 0)
        .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)[0] ?? null;
    return {
      locations: summaries.length,
      totalOnHand,
      totalLotted,
      totalWip: sumBy(summaries, (s) => s.result.totalWipQty ?? 0),
      expiringSoon: sumBy(summaries, (s) => s.result.expiringSoonCount ?? 0),
      expiredWithStock: sumBy(summaries, (s) => s.result.expiredWithStockCount ?? 0),
      // Summed across locations but never into totalOnHand - see the query comment.
      totalUnverified: sumBy(summaries, (s) => s.result.totalUnverifiedQty ?? 0),
      unverifiedLots: sumBy(summaries, (s) => s.result.unverifiedLotCount ?? 0),
      // WIP and the unverified bucket overlap: a claim sits in both until its order ships. Only
      // the server can say how much, so the split is left out entirely while any location's
      // response lacks it (a core that predates the field) rather than shown as all-stock.
      unverifiedInWip: summaries.every((s) => typeof s.result.totalUnverifiedWipQty === 'number')
        ? sumBy(summaries, (s) => s.result.totalUnverifiedWipQty)
        : null,
      // Claims whose order was sent back to the queue, undone or cancelled: out of WIP, not
      // shipped, waiting for a reviewer. Same all-or-nothing rule as above - without it those
      // units fall into "Shipped", which is the wrong answer this field exists to fix.
      unverifiedPendingReview: summaries.every(
        (s) => typeof s.result.totalUnverifiedPendingReviewQty === 'number'
      )
        ? sumBy(summaries, (s) => s.result.totalUnverifiedPendingReviewQty)
        : null,
      totalLots: sumBy(summaries, (s) => s.result.lots?.length ?? 0),
      nearestLot,
      lottingPct: totalOnHand > 0 ? Math.round((totalLotted / totalOnHand) * 100) : null,
    };
  }, [summaries]);

  // Advisory only — the recall mutation holds the real lock; this just avoids a doomed dialog.
  const recall = useMemo(() => {
    const totalWip = aggregates?.totalWip ?? 0;
    // Per LOCATION, matching the backend lock and the dialog's own guard: one busy or count-locked
    // pharmacy must not close the door on a free one.
    const recallable = summaries.filter(
      ({ item, result }) =>
        !item.isLockForCount && !result?.recallInProgress && (result?.totalWipQty ?? 0) > 0
    );
    const inProgress = summaries.some(({ result }) => result?.recallInProgress);
    const allLocked = summaries.length > 0 && summaries.every(({ item }) => item.isLockForCount);

    let tooltip = 'Return reserved stock to inventory';
    if (recallable.length === 0) {
      if (allLocked) tooltip = 'Locked for physical count';
      else if (inProgress) tooltip = 'A recall is already running for this drug';
      else tooltip = 'No reserved stock to return';
    }

    return {
      totalWip,
      inProgress,
      allLocked,
      canRecall: recallable.length > 0,
      tooltip,
    };
  }, [aggregates, summaries]);

  const hasAttention =
    aggregates &&
    (aggregates.expiredWithStock > 0 ||
      aggregates.expiringSoon > 0 ||
      aggregates.totalUnverified > 0);

  // 680 could not fit the lot table's columns (nine since OFFAGE), so the row actions sat past a
  // horizontal scrollbar and the adjust icon was invisible until the operator found it. Wide
  // enough for the columns, capped against the viewport so a narrow screen still shows the panel.
  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 'min(1000px, 94vw)' } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Dark Header */}
        <Box sx={{ bgcolor: HEADER_BG, px: 3, pt: 2.5, pb: 2, color: '#fff', flexShrink: 0 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}
          >
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 11,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              Lot Summary
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={includeDepleted}
                    onChange={(e) => setIncludeDepleted(e.target.checked)}
                    sx={DARK_SWITCH_SX}
                  />
                }
                label={
                  <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Depleted
                  </Typography>
                }
                sx={{ m: 0, display: 'none' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={includeExpired}
                    onChange={(e) => setIncludeExpired(e.target.checked)}
                    sx={DARK_SWITCH_SX}
                  />
                }
                label={
                  <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Expired
                  </Typography>
                }
                sx={{ m: 0 }}
              />
              {/* POM-6087 - the only switch here that NARROWS. A claim means stock left the
                  shelf that inventory never received, and the row has to be found before it
                  can be reconciled; the other switches can only ever add rows to look
                  through. Left enabled when the drug has no claims so the answer is the
                  empty list rather than a control that cannot be pressed. */}
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={onlyUnverified}
                    onChange={(e) => setOnlyUnverified(e.target.checked)}
                    inputProps={{ 'data-testid': 'only-unverified-switch' }}
                    sx={DARK_SWITCH_SX}
                  />
                }
                label={
                  <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {SYSTEM_DEFICIT} only
                  </Typography>
                }
                sx={{ m: 0 }}
              />
              <IconButton
                onClick={onClose}
                size='small'
                sx={{ color: 'rgba(255,255,255,0.8)', ml: 0.5 }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
          </Box>
          <Typography variant='h4' sx={{ fontWeight: 800, mb: 0.5, letterSpacing: -0.5 }}>
            {firstItem?.drugName ?? '—'}
          </Typography>
          <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.75)', mb: 0.5 }}>
            <Box component='span' sx={{ color: 'rgba(255,255,255,0.45)' }}>
              NDC:{' '}
            </Box>
            {firstItem?.ndc ?? '—'}
            {firstItem?.manufacturer && (
              <>
                <Box component='span' sx={{ color: 'rgba(255,255,255,0.25)', mx: 1 }}>
                  ·
                </Box>
                <Box component='span' sx={{ color: 'rgba(255,255,255,0.45)' }}>
                  Manufacturer:{' '}
                </Box>
                {firstItem.manufacturer}
              </>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.5)' }}>
              as of {format(new Date(), 'MMMM d, yyyy')}
            </Typography>
            {aggregates && !loading && (
              <>
                <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.25)' }}>
                  ·
                </Typography>
                <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {multi ? `${aggregates.locations} locations · ` : ''}
                  {aggregates.totalLots} lots · {aggregates.totalOnHand} units
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Stats Bar */}
        {aggregates && (
          <Box
            sx={{
              display: 'flex',
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            {multi && <StatColumn label='Locations' value={aggregates.locations} />}
            <StatColumn label='Total On Hand' value={aggregates.totalOnHand} />
            <StatColumn label='Available' value={aggregates.totalLotted} />
            <StatColumn
              label='WIP'
              value={aggregates.totalWip}
              breakdown={
                aggregates.unverifiedInWip != null && aggregates.totalWip > 0
                  ? [
                      { label: SYSTEM_DEFICIT, value: aggregates.unverifiedInWip },
                      {
                        label: 'Stock',
                        value: Math.max(0, aggregates.totalWip - aggregates.unverifiedInWip),
                      },
                    ]
                  : undefined
              }
            />
            <StatColumn
              label={SYSTEM_DEFICIT}
              value={aggregates.totalUnverified}
              color={aggregates.totalUnverified > 0 ? 'warning.dark' : undefined}
              breakdown={
                aggregates.unverifiedInWip != null && aggregates.totalUnverified > 0
                  ? [
                      { label: 'In WIP', value: aggregates.unverifiedInWip },
                      ...(aggregates.unverifiedPendingReview != null
                        ? [{ label: 'Pending Review', value: aggregates.unverifiedPendingReview }]
                        : []),
                      {
                        label: 'Shipped',
                        value: Math.max(
                          0,
                          aggregates.totalUnverified -
                            aggregates.unverifiedInWip -
                            (aggregates.unverifiedPendingReview ?? 0)
                        ),
                      },
                    ]
                  : undefined
              }
            />
            <StatColumn
              label='Expiring Soon'
              value={aggregates.expiringSoon}
              color={aggregates.expiringSoon > 0 ? 'warning.main' : undefined}
            />
            <StatColumn
              label='Expired w/Stk'
              value={aggregates.expiredWithStock}
              color={aggregates.expiredWithStock > 0 ? 'error.main' : undefined}
            />
          </Box>
        )}

        {/* ONE entry point, scope chosen inside the dialog — a per-lot icon would make the
            operator infer the scope from where they clicked. */}
        {aggregates && hasAccess.recallLotAllocations && (
          <Box
            data-testid='recall-strip'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 2,
              py: 1.25,
              bgcolor: recall.inProgress ? colors.inventoryLockOrangeLight : 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              {recall.inProgress ? (
                <Box component='span' sx={{ color: colors.inventoryLockOrange, fontWeight: 700 }}>
                  Recall in progress
                </Box>
              ) : (
                <>
                  <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {recall.totalWip} units
                  </Box>
                  {' reserved'}
                  {multi ? ` across ${aggregates.locations} locations` : ''}
                </>
              )}
            </Typography>
            <Tooltip title={recall.tooltip}>
              <span>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<SettingsBackupRestoreIcon fontSize='small' />}
                  disabled={!recall.canRecall}
                  onClick={() => setRecallOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Return Allocated Stock to Inventory
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}

        {/* Attention Banner */}
        {hasAttention && !loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.25,
              bgcolor: '#fff3e0',
              borderBottom: '1px solid',
              borderColor: 'warning.light',
              flexShrink: 0,
            }}
          >
            <WarningAmberIcon color='warning' fontSize='small' />
            <Typography variant='body2' sx={{ fontWeight: 700, color: 'warning.dark' }}>
              Attention required
            </Typography>
            {aggregates.expiredWithStock > 0 && (
              <Typography variant='body2' color='text.secondary'>
                <Box component='span' sx={{ fontWeight: 700, color: 'error.main' }}>
                  {aggregates.expiredWithStock}
                </Box>
                {' expired w/ stock'}
              </Typography>
            )}
            {aggregates.expiringSoon > 0 && (
              <Typography variant='body2' color='text.secondary'>
                <Box component='span' sx={{ fontWeight: 700, color: 'warning.dark' }}>
                  {aggregates.expiringSoon}
                </Box>
                {' expiring < 30 days'}
              </Typography>
            )}
            {aggregates.totalUnverified > 0 && (
              <Typography variant='body2' color='text.secondary'>
                <Box component='span' sx={{ fontWeight: 700, color: 'warning.dark' }}>
                  {aggregates.totalUnverified}
                </Box>
                {` ${SYSTEM_DEFICIT.toLowerCase()} across ${aggregates.unverifiedLots} lot`}
                {aggregates.unverifiedLots === 1 ? '' : 's'}
              </Typography>
            )}
          </Box>
        )}

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {!loading && summaries.length === 0 && isOpen && (
            <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 6 }}>
              No lot data found for this item.
            </Typography>
          )}
          {!loading &&
            summaries.length > 0 &&
            summaries.map(({ item, result }) => (
              <PharmacySection
                key={item.inventoryItemId}
                item={item}
                result={result}
                onAdjust={(lot, lotItem) => setAdjustTarget({ lot, item: lotItem })}
              />
            ))}
        </Box>

        {/* Footer */}
        {aggregates && !loading && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1.5,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {aggregates.nearestLot ? (
              <Typography variant='caption' color='text.secondary'>
                Next expiry in{' '}
                <Box
                  component='span'
                  sx={{
                    fontWeight: 700,
                    color: aggregates.nearestLot.isExpiringSoon ? 'warning.main' : 'text.primary',
                  }}
                >
                  {aggregates.nearestLot.daysUntilExpiry}d
                </Box>
                {' · '}
                <Box component='span' sx={{ fontFamily: 'monospace' }}>
                  {aggregates.nearestLot.lotNumber}
                </Box>
              </Typography>
            ) : (
              <Typography variant='caption' color='text.secondary'>
                No active lots
              </Typography>
            )}
            {aggregates.lottingPct !== null && (
              <>
                <Typography variant='caption' sx={{ color: 'text.disabled' }}>
                  ·
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {aggregates.lottingPct}%
                  </Box>
                  {' of stock lotted'}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      <ManualAdjustmentDialog
        open={adjustTarget !== null}
        lot={adjustTarget?.lot ?? null}
        drugName={adjustTarget?.item?.drugName ?? ''}
        ndc={adjustTarget?.item?.ndc ?? ''}
        fulfillingPharmacyId={adjustTarget?.item?.fulfillingPharmacy?.fulfillingPharmacyId ?? ''}
        facilityName={adjustTarget?.item?.fulfillingPharmacy?.name ?? ''}
        onSaveSuccess={handleAdjustSuccess}
        onClose={() => setAdjustTarget(null)}
      />

      <RecallAllocationsDialog
        open={recallOpen}
        summaries={summaries}
        onRecallSuccess={handleRecallSuccess}
        onClose={() => setRecallOpen(false)}
      />
    </Drawer>
  );
};

LotSummaryDrawer.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      inventoryItemId: PropTypes.string,
      ndc: PropTypes.string,
      drugName: PropTypes.string,
      manufacturer: PropTypes.string,
      fulfillingPharmacy: PropTypes.shape({
        fulfillingPharmacyId: PropTypes.string,
        name: PropTypes.string,
      }),
      isLockForCount: PropTypes.bool,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onAdjusted: PropTypes.func,
};
LotSummaryDrawer.defaultProps = { items: [], onAdjusted: undefined };

export default LotSummaryDrawer;
